import { motion } from 'framer-motion'

export function Comparison() {
    return (
        <section
            id="comparison"
            className="py-20 px-6 md:px-12 lg:px-16"
        >
            <div className="w-full max-w-7xl mx-auto">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center mb-12"
                >
                    <span className="inline-block font-body text-xs uppercase tracking-widest text-muted-foreground border border-border rounded-full px-4 py-1.5 mb-6">
                        The Shift
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
                        What Changes With Poly.
                    </h2>
                    <p className="font-body text-lg text-muted-foreground font-light max-w-2xl mx-auto">
                        The gap between managing everything yourself and having a workforce that handles it.
                    </p>
                </motion.div>

                {/* Comparison Grid */}
                <div className="grid grid-cols-1 gap-4">
                    {comparisons.map((item, index) => (
                        <ComparisonRow key={index} {...item} index={index} />
                    ))}
                </div>
            </div>
        </section>
    )
}

interface ComparisonItem {
    without: string
    with_poly: string
    image: string
}

const comparisons: ComparisonItem[] = [
    {
        without: "You manually respond to DMs across 4 platforms. Miss leads at 2am. Lose deals to slow response time.",
        with_poly: "Digital receptionist responds in under 60 seconds, 24/7. Qualifies leads, books meetings, adds to CRM automatically.",
        image: "/platform_screenshots/conversations.jpg"
    },
    {
        without: "Content takes hours. You brainstorm, write, design, schedule, repeat. It never compounds because you can't be consistent.",
        with_poly: "Content workers generate, design, and publish on schedule. Posts go out while you sleep — every day, on every platform.",
        image: "/platform_screenshots/creative-studio.jpg"
    },
    {
        without: "Lead gen means cold outreach tools, spreadsheets, and manual follow-up. Half your prospects go stale before you reach them.",
        with_poly: "Workers source prospects, score by ICP fit, run campaigns, and move leads through your pipeline automatically.",
        image: "/platform_screenshots/prospects-kanban.jpg"
    },
    {
        without: "Running ads requires a media buyer or hours of your own time in Ads Manager. Testing creatives is slow and expensive.",
        with_poly: "From idea to active ad campaign in 6 minutes. Workers generate creatives, set budgets, and launch across Meta — in parallel.",
        image: "/platform_screenshots/ads-manager.jpg"
    },
    {
        without: "Scaling means hiring. More headcount, more overhead, more management. Every new hire takes months to ramp.",
        with_poly: "Add workers instantly. No onboarding, no PTO, no turnover. Scale from 1 to 100 workers without scaling headcount.",
        image: "/platform_screenshots/workers.jpg"
    },
    {
        without: "You don't know what's working. Analytics are scattered across 6 tools. Attribution is a guess.",
        with_poly: "Every workflow tracked to cost, work budget, and business outcome. See exactly what drives revenue — per worker, per task.",
        image: "/platform_screenshots/operations-overview.jpg"
    }
]

function ComparisonRow({ without, with_poly, image, index }: ComparisonItem & { index: number }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* WITHOUT */}
            <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="p-5 rounded-2xl border border-border/50 bg-card/30 flex items-center"
            >
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="font-heading text-xs text-muted-foreground/60 uppercase tracking-wider">Without</span>
                    </div>
                    <p className="font-body text-sm text-muted-foreground font-light leading-relaxed">{without}</p>
                </div>
            </motion.div>

            {/* WITH POLY */}
            <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: index * 0.05 + 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-2xl border border-primary/30 min-h-[180px]"
            >
                <motion.img
                    src={image}
                    alt={with_poly}
                    initial={{ scale: 1.1 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover object-top opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="font-heading text-xs text-primary uppercase tracking-wider">With Poly</span>
                    </div>
                    <p className="font-body text-sm text-foreground/90 font-light leading-relaxed">{with_poly}</p>
                </div>
            </motion.div>
        </div>
    )
}
